import { motion } from 'framer-motion'
import { Brain, Cpu, LineChart, Zap, Target, TrendingUp } from 'lucide-react'
import { fadeIn, staggerContainer } from '../utils/motion'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

const AISolutions = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)

  const solutions = [
    {
      icon: Brain,
      titleKey: 'aiSolutions.solution1Title',
      descriptionKey: 'aiSolutions.solution1Desc'
    },
    {
      icon: LineChart,
      titleKey: 'aiSolutions.solution2Title',
      descriptionKey: 'aiSolutions.solution2Desc'
    },
    {
      icon: Cpu,
      titleKey: 'aiSolutions.solution3Title',
      descriptionKey: 'aiSolutions.solution3Desc'
    }
  ]

  const benefits = [
    {
      icon: Zap,
      titleKey: 'aiSolutions.benefit1Title',
      descriptionKey: 'aiSolutions.benefit1Desc'
    },
    {
      icon: Target,
      titleKey: 'aiSolutions.benefit2Title',
      descriptionKey: 'aiSolutions.benefit2Desc'
    },
    {
      icon: TrendingUp,
      titleKey: 'aiSolutions.benefit3Title',
      descriptionKey: 'aiSolutions.benefit3Desc'
    }
  ]

  return (
    <div className="pt-26">
      {/* Hero Section avec image */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 to-purple-700 text-white overflow-hidden">
        {/* Image de fond */}
        <div className="absolute inset-0">
          <img
            src="/images/ai-hero.webp"
            alt="Solutions IA"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-blue-900/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              {t('aiSolutions.pageTitle')}
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              {t('aiSolutions.pageSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
          >
            <motion.div variants={fadeIn('up', 0.2)} className="text-center mb-16">
              <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">
                {t('aiSolutions.collaborationTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('aiSolutions.collaborationSubtitle')}
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {solutions.map((solution, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn('up', index * 0.1)}
                  className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 mb-6">
                    <solution.icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
                    {t(solution.titleKey)}
                  </h3>
                  <p className="text-gray-600">{t(solution.descriptionKey)}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section avec image au milieu */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contenu Bénéfices */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
            >
              <motion.div variants={fadeIn('up', 0.2)} className="mb-8">
                <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">
                  {t('aiSolutions.benefitsTitle')}
                </h2>
                <p className="text-xl text-gray-600">
                  {t('aiSolutions.benefitsSubtitle')}
                </p>
              </motion.div>

              <motion.div
                variants={staggerContainer}
                className="space-y-6"
              >
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    variants={fadeIn('up', index * 0.1)}
                    className="flex items-start space-x-4 p-6 bg-white rounded-xl hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex-shrink-0">
                      <benefit.icon size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">
                        {t(benefit.titleKey)}
                      </h3>
                      <p className="text-gray-600">{t(benefit.descriptionKey)}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Image au milieu */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/ai-mid.webp"
                  alt="Intelligence Artificielle Industrielle"
                  className="w-full h-[500px] object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                
                {/* Badge informatif */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  className="absolute bottom-6 left-6 bg-white rounded-xl p-4 shadow-lg"
                >
                  <div className="flex items-center space-x-2">
                    <Brain className="w-5 h-5 text-purple-600" />
                    <span className="font-bold text-gray-900 text-sm">{t('aiSolutions.industrialAI')}</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              {t('aiSolutions.optimizationTitle')}
            </h2>
            <p className="text-xl">
              {t('aiSolutions.optimizationText')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-10 rounded-2xl">
              <h3 className="text-3xl font-heading font-bold text-gray-900 mb-6 text-center">
                {t('aiSolutions.approachTitle')}
              </h3>
              <p className="text-lg text-gray-700 text-center leading-relaxed">
                {t('aiSolutions.approachText')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default AISolutions